import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { saveQuiz } from '@redux/quiz/actions';
import { quizFormNextStep } from '@redux/quiz/actions';
import { useDispatch } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';

import Section from '@common/Section';
import Container from '@common/Container';
import Row from '@common/Row';
import Heading from '@common/Heading';
import RichText from '@common/RichText';
import Video from '@common/Video';
import Buttons from '@common/blocks/flex/Buttons';
import DemoQuiz from '@common/modal/demo-quiz/DemoQuiz';

import Attendance from '@images/homepage/new-quiz/attendance.svg';
import Book from '@images/homepage/new-quiz/book.svg';
import Diploma from '@images/homepage/new-quiz/diploma.svg';
import Message from '@images/homepage/new-quiz/message.svg';
import Money from '@images/homepage/new-quiz/money.svg';
// import Pencil from '@images/homepage/new-quiz/pencil.svg';
// import Reports from '@images/homepage/new-quiz/reports.svg';
import Waitlist from '@images/homepage/new-quiz/waitlist.svg';

const options = [
  { 
    id: 'prodQuizGoals7Attendance',
    key: 'Center admin & reports',
    image: Attendance,
    color: 'teal',
    value: 'core'
  },
  { 
    id: 'prodQuizGoals3Admin',
    key: 'Billing & payments',
    image: Money,
    color: 'yellow',
    value: 'core'
  },
  { 
    id: 'prodQuizGoals6LessonPlanning',
    key: 'Curriculum & materials',
    image: Book,
    color:'red',
    value: 'digital curriculum'
  },
  { 
    id: 'prodQuizGoals2ParentComms',
    key: 'Family communication',
    image: Message,
    color: 'green',
    value: 'core'
  },
  {
    id: 'prodQuizGoals8Insights',
    key: 'Enrollment management',
    image: Waitlist,
    color: 'teal',
    value: 'core'
  },
  {
    id: 'prodQuizGoals4TeacherSkills',
    key: 'Educator training hours',
    image: Diploma,
    color: 'yellow',
    value: 'academy'
  }
];

import * as styles from './video.module.scss';

const FullWidthVideoQuiz = ({ data }) => {
  const {
    text,
    textAlignment,
    textColWidth,
    images,
    video,
    buttons,
    topMargin,
    bottomMargin
  } = data;
  const { control, register, formState: { errors }, handleSubmit, watch } = useForm({
    defaultValues: {
      recommend: [{}]
    }
  });
  const { fields } = useFieldArray({
    control,
    name: 'recommend',
  });
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const dispatch = useDispatch();
  const onSubmit = (data) => {
    setShow(true);
    dispatch(saveQuiz(data));
    dispatch(quizFormNextStep());
  };
  const onChange = (data) => {
    dispatch(saveQuiz(data));
  }
  useEffect(() => {
    const subscription = watch((value) => onChange(value));
    return () => subscription.unsubscribe();
  }, [watch]);
  return (
    <Section spacing={clsx(bottomMargin ? bottomMargin : 'mb-7')}>
      {/* Desktop View */}
      <div className='d-none d-lg-flex justify-content-center position-relative'>
        {/* Background video loop */}
        {images &&
          <video autoPlay loop muted playsInline className={styles.videoPlayer}>
            <source src={images[0]?.file?.url} type='video/mp4'/>
            <track kind='captions'></track>
          </video>
        }
        <Container className={clsx('position-absolute', styles.logoContainer)}>
          <Row className={clsx('col mx-auto', textColWidth, {
            'text-center': textAlignment === 'Center',
            'text-end': textAlignment === 'Right',
            'text-start': textAlignment === 'Left',
          })}>
            {text &&
              <RichText
                json={text}
                h1='mb-3'
                h2='mb-3'
                p='mb-3'
                a='fs-16'
              />
            }
          </Row>
          <Row className='mb-3'>
            <Form noValidate onSubmit={handleSubmit(onSubmit)}>
              <Row className='col-6 col-xl-10 row-cols-lg-3 row-cols-xl-6 gy-3 mx-auto mb-3'>
                {options.map((item,index) => (
                  <div key={index} className='d-flex demo-quiz-checkbox'>
                    {fields.map((field) => (
                      <Form.Check key={field.id} className='d-flex flex-fill p-0'>
                        <Form.Check.Label className='d-flex flex-column flex-fill bg-white text-center border border-3 py-3 pt-xl-3 pb-xl-2 px-1 border-black rounded-3 position-relative' style={{cursor: 'pointer'}}>
                          {item?.image &&
                            <div className='d-flex justify-content-center'>
                              <img className={`rounded bg-${item.color}`} src={item.image} alt={item.key} style={{ aspectRatio: '1 / 1', padding: 5, height: 30, width: 30 }}/>
                            </div>
                          }
                          <Form.Check.Input type={'checkbox'} style={{position: 'absolute', left: 39, top: 5}} value={item.id} {...register(`recommend`, { validate: v => v.length > 0 && v[0].constructor !== Object })}/>
                          <p className='mt-2 mb-0 fs-12'>{item.key}</p>
                        </Form.Check.Label>
                      </Form.Check>
                    ))}
                  </div>
                ))}
              </Row>
              {errors.recommend &&
                <Alert variant={'primary'}>
                  {'At least one option is required'}
                </Alert>
              }
              <div className='d-flex align-items-center justify-content-center'>
                <button type='submit' className='btn btn-primary' style={{minWidth: 228.48}}>{'Learn how Lillio can help'}</button>
              </div>
            </Form>
            <DemoQuiz data={{size: 'lg-plus'}} show={show} close={handleClose} step={2}/>
          </Row>
        </Container>
      </div>
      {/* Mobile View */}
      <Container className={clsx('d-lg-none', topMargin)}>
        <Row>
          {/* Prevents mobile text from showing duplicate h1 values */}
          {text && documentToReactComponents(JSON.parse(text.raw), {
            renderText: text =>
              text
                .split('\n')
                .flatMap((text, i) => [i > 0 && <br key={i} />, text]),
            renderNode: {
              [BLOCKS.HEADING_1]: (node, children) =>
                // Renders h2 with h1 styles
                <h2 className={'h1 mb-3'}>{children}</h2>
              ,
              [BLOCKS.PARAGRAPH]: (node, children) => {
                if (node.content.every(x => x.nodeType === 'text' && x.value === '')) {
                  // Fix for blank paragraph blocks adding margin
                  return <>{children}</>;
                }
                return (
                  <p className={'fs-24 mb-3'}>{children}</p>
                )
              },
            }
          })}
          {buttons &&
            <Buttons data={data} />
          }
        </Row>
      </Container>
    </Section>
  )
}

export default FullWidthVideoQuiz
